import type { TransferPhoneSelectorProps } from './index.types';
import { useTransferContactNumbers } from 'Modules/softphone/hooks/useTransferContactNumbers';
import { useSoftphoneStore } from 'Modules/softphone/store';
import React from 'react';
import { formatNumber } from 'Utils/phoneUtil';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/lib/ui/select';
import { MAX_VISIBLE_PHONE_NUMBERS } from './index.constants';
import { measureViewportMaxHeight } from './utils';

/**
 * TransferPhoneSelector - Phone number dropdown for the transfer target.
 *
 * The phone numbers come pre-processed from `useTransferContactSearch`
 * (built on top of `FetchContactsV2`, which returns the full contact data).
 * No MobX reactivity is needed here.
 */
export const TransferPhoneSelector = ({
  testID,
}: TransferPhoneSelectorProps): React.ReactElement | null => {
  const componentTestId = testID ? `${testID}-phone-select` : 'phone-select';

  const {
    transferTarget,
    selectedPhoneNumber,
    setTransferSelectedPhoneNumber,
  } = useSoftphoneStore((state) => ({
    transferTarget: state.transferTarget,
    selectedPhoneNumber: state.transferSelectedPhoneNumber,
    setTransferSelectedPhoneNumber: state.setTransferSelectedPhoneNumber,
  }));

  const { phoneNumbers, displayValue } = useTransferContactNumbers(
    transferTarget,
    selectedPhoneNumber,
    setTransferSelectedPhoneNumber
  );

  const viewportRef = React.useRef<HTMLDivElement | null>(null);
  const [maxHeight, setMaxHeight] = React.useState<number | undefined>(
    undefined
  );

  // On open, cap the viewport at the height of the first 3 items so the
  // dropdown shows at most 3 numbers and scrolls when there are more.
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      return;
    }

    requestAnimationFrame(() => {
      setMaxHeight(
        measureViewportMaxHeight(viewportRef.current, MAX_VISIBLE_PHONE_NUMBERS)
      );
    });
  };

  // Hide selector entirely for direct number transfers (no contact match)
  if (phoneNumbers.length === 0 || transferTarget?.type === 'number') {
    return null;
  }

  const hasSingleNumber = phoneNumbers.length === 1;

  return (
    <Select
      value={selectedPhoneNumber ?? undefined}
      onValueChange={setTransferSelectedPhoneNumber}
      disabled={hasSingleNumber}
      onOpenChange={handleOpenChange}
    >
      <SelectTrigger
        className="w-full border border-border rounded-[4px] px-[8px] py-[8px] h-auto text-[12px] text-foreground disabled:opacity-100"
        data-testid={`${componentTestId}-trigger`}
        aria-label="Select phone number for transfer"
        disabled={hasSingleNumber}
      >
        <SelectValue placeholder="Select a number">{displayValue}</SelectValue>
      </SelectTrigger>
      <SelectContent
        data-testid={`${componentTestId}-content`}
        className="z-[10000]"
        defaultScroll
        viewportRef={viewportRef}
        viewportStyle={maxHeight ? { maxHeight: `${maxHeight}px` } : undefined}
      >
        {phoneNumbers.map((phone) => (
          <SelectItem
            key={`${phone.label}-${phone.number}`}
            value={phone.number}
            data-testid={`${componentTestId}-option-${phone.number}`}
          >
            {phone.label}:{' '}
            {phone.label === 'Office'
              ? phone.number
              : formatNumber(phone.number)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

TransferPhoneSelector.displayName = 'TransferPhoneSelector';
